import { useRef } from 'react'
import { lightImpactVibrate } from '../utils/appBridge'

interface UseKeypadClickProps {
  onNumberClick: (num: number) => void
  onBackspaceClick: () => void
}

export const useKeypadClick = ({ onNumberClick, onBackspaceClick }: UseKeypadClickProps) => {
  const keypadRef = useRef<HTMLDivElement>(null)

  // Handle click for iOS (single handler on top div)
  const handleIOSClick = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!keypadRef.current) return

    event.preventDefault()
    event.stopPropagation()

    const rect = keypadRef.current.getBoundingClientRect()
    let clickX: number, clickY: number

    if ('touches' in event) {
      const touch = event.touches[0]
      clickX = touch.clientX - rect.left
      clickY = touch.clientY - rect.top
    } else {
      clickX = event.clientX - rect.left
      clickY = event.clientY - rect.top
    }

    // Use requestAnimationFrame to ensure DOM is updated and coordinates are fresh
    requestAnimationFrame(() => {
      // Check if click is directly on an empty div
      const elementAtPoint = document.elementFromPoint(clickX + rect.left, clickY + rect.top)
      if (elementAtPoint && elementAtPoint.getAttribute('data-keypad-empty') === 'true') {
        return // Don't trigger anything for empty divs
      }

      const buttonElements = keypadRef.current!.querySelectorAll('[data-keypad-item]')

      let nearestButton = null
      let minDistance = Infinity

      for (const button of buttonElements) {
        const buttonRect = button.getBoundingClientRect()
        const relativeButtonRect = {
          left: buttonRect.left - rect.left,
          top: buttonRect.top - rect.top,
          right: buttonRect.right - rect.left,
          bottom: buttonRect.bottom - rect.top
        }

        const buttonCenterX = (relativeButtonRect.left + relativeButtonRect.right) / 2
        const buttonCenterY = (relativeButtonRect.top + relativeButtonRect.bottom) / 2

        const distance = Math.sqrt(Math.pow(clickX - buttonCenterX, 2) + Math.pow(clickY - buttonCenterY, 2))

        if (distance < minDistance) {
          minDistance = distance
          nearestButton = button
        }
      }

      if (nearestButton && minDistance < 100) {
        const item = nearestButton.getAttribute('data-keypad-item')

        if (item === 'back') {
          lightImpactVibrate()
          onBackspaceClick()
        } else if (item && !isNaN(Number(item))) {
          lightImpactVibrate()
          onNumberClick(Number(item))
        }
      }
    })
  }

  // Handle click for Android (individual handlers)
  const handleAndroidClick = (item: number | 'back') => {
    return (event: React.TouchEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()

      if (item === 'back') {
        onBackspaceClick()
      } else if (typeof item === 'number') {
        onNumberClick(item)
      }
    }
  }

  return {
    keypadRef,
    handleIOSClick,
    handleAndroidClick
  }
}
